// AI 계약 상담 (alfred, NZSC-3151/3153) 스토어.
// 백엔드 계약(cold-syrup 엔진 + api-public BFF 경유): 세션/메시지/계약서업로드/지갑(KRW)/충전주문.
// 응답 봉투는 단일 `{ code, message, data }`. apiWrapper 가 code===0 에서 data(=페이로드)를 반환하므로
// store 는 res 를 곧바로 페이로드로 사용한다 (과거 이중 data.data 가정 폐기).
import { callGetApi, callPostApi, callDeleteApi } from "@/utils/apiWrapper";
import AcConstant from "@/mixins/alfred/const/alfredAcConstant.js";

export const state = {
  _sessions: [], // SessionResponse[] 사이드바 세션 목록
  _currentSession: null, // 현재 진행 중인 세션 (SessionResponse)
  _messages: [], // 현재 세션의 메시지 목록 (MessageResponse[])
  _wallet: null, // WalletResponse { userNo, balance, totalCharged, totalUsed } (KRW)
  _isAnswering: false, // 답변 로딩 상태
  // 상담 진입 시 바인딩할 리포트/계약서 컨텍스트 (= 리포트당 지갑 키. wallet/충전/업로드 기본값)
  _selectedReportSeq: null,
  _selectedContractSeq: null,
  _selectedCartSeq: null,
};

export const getters = {
  getSessions: state => state._sessions,
  getCurrentSession: state => state._currentSession,
  getMessages: state => state._messages,
  getWallet: state => state._wallet,
  getIsAnswering: state => state._isAnswering,
};

export const mutations = {
  SET_SESSIONS(state, list) {
    state._sessions = Array.isArray(list) ? list : [];
  },
  SET_CURRENT_SESSION(state, session) {
    state._currentSession = session;
  },
  SET_MESSAGES(state, messages) {
    state._messages = Array.isArray(messages) ? messages : [];
  },
  ADD_MESSAGE(state, message) {
    state._messages.push(message);
  },
  // messageSeq 기준으로 메시지 필드 갱신 (예: 별점 반영)
  UPDATE_MESSAGE(state, { messageSeq, patch }) {
    const idx = state._messages.findIndex(m => m.messageSeq === messageSeq);
    if (idx > -1) {
      state._messages.splice(idx, 1, { ...state._messages[idx], ...patch });
    }
  },
  SET_WALLET(state, wallet) {
    state._wallet = wallet;
  },
  SET_IS_ANSWERING(state, val) {
    state._isAnswering = val;
  },
  SET_SELECTED_CONTEXT(state, { reportSeq = null, contractSeq = null, cartSeq = null }) {
    state._selectedReportSeq = reportSeq;
    state._selectedContractSeq = contractSeq;
    state._selectedCartSeq = cartSeq;
  },
  // 리포트당 지갑 키만 갱신(contractSeq 보존). wallet/충전/업로드 기본 컨텍스트.
  SET_WALLET_CONTEXT(state, { reportSeq = null, cartSeq = null }) {
    state._selectedReportSeq = reportSeq;
    state._selectedCartSeq = cartSeq;
  },
  RESET_ALFRED(state) {
    state._currentSession = null;
    state._messages = [];
    state._isAnswering = false;
    state._selectedReportSeq = null;
    state._selectedContractSeq = null;
    state._selectedCartSeq = null;
  },
};

export const actions = {
  // 세션 목록 (사이드바)
  async acGetSessions({ commit }, { limit = 30, offset = 0 } = {}) {
    const res = await callGetApi({
      url: AcConstant.GET_SESSION_LIST,
      params: { limit, offset },
      commit,
    });
    if (res) commit("SET_SESSIONS", res);
    return res;
  },

  // 세션 + 메시지 상세
  async acGetSessionDetail({ commit }, sessionSeq) {
    const res = await callGetApi({
      url: `${AcConstant.GET_SESSION_DETAIL}/${sessionSeq}`,
      commit,
    });
    if (res) {
      commit("SET_CURRENT_SESSION", res.session);
      commit("SET_MESSAGES", res.messages);
    }
    return res;
  },

  // 세션 생성 (리포트/계약서 바인딩 가능). cartSeq = reportSeq 없는 cart 기반 리포트 식별자.
  // reportPrompt = FE(aiPromptBuilder)가 생성한 리포트 컨텍스트. cold 세션 report_prompt 에 바인딩 → 답변 시 주입.
  async acCreateSession(
    { commit },
    { reportSeq = null, cartSeq = null, contractSeq = null, reportPrompt = null } = {},
  ) {
    const res = await callPostApi({
      url: AcConstant.POST_SESSION,
      data: {
        reportSeq,
        contractSeq,
        ...(cartSeq != null ? { cartSeq } : {}),
        ...(reportPrompt ? { reportPrompt } : {}),
      },
      commit,
    });
    if (res) commit("SET_CURRENT_SESSION", res);
    return res;
  },

  // 세션에 리포트/계약서 바인딩. reportPrompt 전달 시 리포트 컨텍스트 보강(resume self-heal).
  // reportSeq/contractSeq 는 서버에서 항상 덮어쓰므로 호출부가 기존 값을 함께 넘겨 보존해야 한다.
  async acBindSession(
    { commit },
    { sessionSeq, reportSeq = null, contractSeq = null, reportPrompt = null },
  ) {
    const res = await callPostApi({
      url: `${AcConstant.POST_SESSION_BIND}/${sessionSeq}/bind`,
      data: {
        reportSeq,
        contractSeq,
        ...(reportPrompt ? { reportPrompt } : {}),
      },
      commit,
    });
    if (res) commit("SET_CURRENT_SESSION", res);
    return res;
  },

  async acDeleteSession({ commit }, sessionSeq) {
    const res = await callDeleteApi({
      url: `${AcConstant.DELETE_SESSION}/${sessionSeq}`,
      commit,
    });
    return res;
  },

  // 메시지 전송 (멀티턴). sessionSeq 미지정 시 BE 가 최근 active 세션 재사용/신규 생성.
  // 응답(SendMessageResponse)에 답변 + needsCharge(boolean) + tokenBalance(KRW 잔액) + tokenCharged(차감 KRW).
  async acSendMessage({ commit, state }, { message, contractSeq = null }) {
    commit("SET_IS_ANSWERING", true);
    try {
      const res = await callPostApi({
        url: AcConstant.POST_MESSAGE,
        data: {
          sessionSeq: state._currentSession ? state._currentSession.sessionSeq : undefined,
          message,
          // 이번 턴 첨부 PDF(있으면 BE가 세션 바인딩 + user 메시지에 기록 → 말풍선 칩)
          ...(contractSeq != null ? { contractSeq } : {}),
        },
        commit,
      });
      return res; // SendMessageResponse | null
    } finally {
      commit("SET_IS_ANSWERING", false);
    }
  },

  // 답변 별점 (1~5) — 랜덤 5점 팝업 전용
  async acRateMessage({ commit }, { messageSeq, rating, comment = null }) {
    const res = await callPostApi({
      url: `${AcConstant.POST_MESSAGE_RATING}/${messageSeq}/rating`,
      data: { rating, comment },
      commit,
    });
    if (res) commit("UPDATE_MESSAGE", { messageSeq, patch: { rating } });
    return res;
  },

  // 답변별 thumbs(좋아요=1/싫어요=-1/취소=0) — 별점 rating 과 별도 수집.
  async acThumbMessage({ commit }, { messageSeq, thumb }) {
    const res = await callPostApi({
      url: `${AcConstant.POST_MESSAGE_RATING}/${messageSeq}/thumb`,
      data: { thumb },
      commit,
    });
    if (res) {
      commit("UPDATE_MESSAGE", {
        messageSeq,
        patch: { thumb: thumb === 0 ? null : thumb },
      });
    }
    return res;
  },

  // 계약서 업로드 (PDF 정본만 accepted). 이미지/스캔본은 gate 에서 거절.
  // callPostFormDataApi 는 단일봉투를 풀어 { data, errorCode, message, ok } 로 정규화한다.
  // 거절 사유는 data.rejectReason / accepted=false 로 판단.
  // reportSeq/cartSeq = 리포트당 지갑 키(둘 중 하나 필수). Opus 비전 파싱 실비가 그 리포트 지갑에서 차감됨.
  async acUploadContract(
    { commit, state },
    { fileBase64, fileName, mimeType = null, reportSeq = null, cartSeq = null },
  ) {
    // fileBase64 는 문자열이라 JSON 으로 전송(BFF @Body 파싱). multipart 아님(BFF에 multer 없음).
    // 반환: code===0 이면 cold 페이로드({ accepted, contractSeq, parsed, rejectReason }) 직접, 실패 시 null.
    // reportSeq/cartSeq 미지정 시 store 선택 컨텍스트 사용(Opus 비전 실비가 그 리포트 지갑에서 차감).
    const rk = reportSeq != null ? reportSeq : state._selectedReportSeq;
    const ck = cartSeq != null ? cartSeq : state._selectedCartSeq;
    const res = await callPostApi({
      url: AcConstant.POST_CONTRACT,
      data: {
        fileBase64,
        fileName,
        mimeType,
        ...(rk != null ? { reportSeq: rk } : {}),
        ...(ck != null ? { cartSeq: ck } : {}),
      },
      commit,
    });
    return res;
  },

  // 현재 상담의 리포트당 지갑 키를 store에 세팅 → 이후 wallet/충전/업로드가 기본값으로 사용.
  acSetWalletContext({ commit }, { reportSeq = null, cartSeq = null }) {
    commit("SET_WALLET_CONTEXT", {
      reportSeq: reportSeq != null ? reportSeq : null,
      cartSeq: cartSeq != null ? cartSeq : null,
    });
  },

  // 지갑(토큰 잔액): { userNo, balance, totalCharged, totalUsed }. 리포트당.
  // reportSeq/cartSeq 미지정 시 store 선택 컨텍스트(_selectedReportSeq/_selectedCartSeq) 사용.
  async acGetWallet({ commit, state }, { reportSeq = null, cartSeq = null } = {}) {
    const rk = reportSeq != null ? reportSeq : state._selectedReportSeq;
    const ck = cartSeq != null ? cartSeq : state._selectedCartSeq;
    // 리포트 컨텍스트가 전혀 없으면 BE가 거부(2510)하므로 호출 자체를 스킵(빈 지갑 유지).
    if (rk == null && ck == null) return null;
    const res = await callGetApi({
      url: AcConstant.GET_WALLET,
      params: {
        ...(rk != null ? { reportSeq: rk } : {}),
        ...(ck != null ? { cartSeq: ck } : {}),
      },
      commit,
    });
    if (res) commit("SET_WALLET", res);
    return res;
  },

  // 충전 주문 생성 (POST /api/v1/alfred/order).
  // 결제 트리거(PG SDK)에 필요한 merchant_uid 등 주문 페이로드를 반환.
  // PG 결제 성공 후 백엔드 웹훅이 잔액을 확정하므로, FE 는 완료 후 acGetWallet 으로 재조회한다.
  // BFF 는 packageCode(mini/basic/plus)만 받고 금액은 서버 SSOT로 결정한다(클라 금액 미신뢰, L1).
  async acCreateOrder(
    { commit, state },
    { packageCode, pgName, source = null, reportSeq = null, cartSeq = null },
  ) {
    // 충전이 그 리포트 지갑으로만 들어가게 — 미지정 시 store 선택 컨텍스트 사용.
    const rk = reportSeq != null ? reportSeq : state._selectedReportSeq;
    const ck = cartSeq != null ? cartSeq : state._selectedCartSeq;
    const res = await callPostApi({
      url: AcConstant.POST_ORDER,
      data: {
        packageCode,
        pgName,
        ...(source ? { source } : {}),
        ...(rk != null ? { reportSeq: rk } : {}),
        ...(ck != null ? { cartSeq: ck } : {}),
      },
      commit,
    });
    return res; // { merchantUid, userNo, userName, email, mobile, ... } | null
  },

  // 페이플(카드) PC 결제 확정. 카카오는 포트원 웹훅이 grant 하지만, 페이플은 웹훅이 없어
  // 결제 콜백 후 FE 가 이걸 호출해야 서버가 cPay 재검증 + 토큰 충전(grant)을 한다(L1).
  async acCompletePaypleOrder({ commit }, { merchantUid }) {
    const res = await callPostApi({
      url: AcConstant.POST_ORDER_PAYPLE_COMPLETE,
      data: { merchant_uid: merchantUid },
      commit,
    });
    return res; // { status } | null
  },

  // 카카오(포트원) 동기 확정 — 서버가 getPayment 로 PAID 검증 후 grant(멱등). 웹훅과 이중 안전.
  async acCompleteOrder({ commit }, { merchantUid }) {
    const res = await callPostApi({
      url: AcConstant.POST_ORDER_COMPLETE,
      data: { merchant_uid: merchantUid },
      commit,
    });
    return res; // { status } | null
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
